import store from 'state/store.js';
import { isTest } from 'variables/host';
import { round } from 'utils/round-functions/round';
import { ResponseStatus } from '../../variables/response-status';
import { normalizeAdditionalFields } from './normalizeAdditionalFields';
import { normalizeBooleanFlag } from './normalizeBooleanFlag';
import { getActiveIntent, resolveTransactionType } from './intentMapping';
import { isNativeSdkMethodKey } from '../../utils/hooks/digital-wallets/native-sdk';

const MANUAL_BANK_WIRE_PROCESSOR_TYPE = 'manual_bank_wire';

const mapAvailableCurrenciesFees = (availableCurrencies = {}) => {
  const emptyFee = {
    flat_fee_amount: null,
    is_included: false,
    max_fee_amount: null,
    max_transaction_amount: null,
    min_fee_amount: null,
    min_transaction_amount: null,
    percentage_fee: null,
  };
  return {
    ...availableCurrencies,
    currencies: availableCurrencies.currencies?.map((currency) => {
      const currencyFees = currency.fees || [];
      return {
        ...currency,
        fees: currency.has_payment_method_without_fee ? [emptyFee, ...currencyFees] : currencyFees,
      };
    }),
  };
};

export const mapGetUserGraphQlData = (graphQLData) => {
  const state = store.getState();
  const {
    initTheme,
    initTheme: {
      sessionInformation,
      applicationConfiguration = {},
      theme_editor_settings: { customization_settings: { layout } = {} } = {},
    },
    profileInfo: { transaction_type },
  } = state;
  const isAmountFirstLayout = layout === 'amount-first';

  const { customer, available_currencies, last_transaction, promocode_options = [] } = graphQLData;
  const sessionPromocodeOptions =
    sessionInformation?.session_configuration?.promocode_options || [];
  const promocodeOptions =
    promocode_options.length > 0 ? promocode_options : sessionPromocodeOptions;

  // The single Panoramic 2.0-only mapping step: the session-wide transaction
  // type is derived from the active intent (the primary intent until the
  // player switches tab), not copied straight from the session reply. For a
  // single-intent session the active intent always equals the primary intent,
  // and an intent this application does not recognise keeps the session
  // reply's own transaction type, so the result is identical to today's.
  const activeIntent = getActiveIntent(state);
  const activeTransactionType = resolveTransactionType(
    activeIntent,
    sessionInformation?.transaction_type,
  );

  return available_currencies?.currencies?.length || !isAmountFirstLayout
    ? {
        base_amount: sessionInformation?.amount ?? '',
        base_currency: available_currencies?.base_currency ?? sessionInformation?.currency ?? '',
        frontend_name: sessionInformation?.frontend_name,
        transaction_type: activeTransactionType,
        order_id: sessionInformation?.order_id,
        agent_name: sessionInformation?.agent_name,
        intent: activeIntent,
        session_type: sessionInformation?.session_type,
        remove_customer_data: normalizeBooleanFlag(applicationConfiguration?.remove_customer_data),
        request_email: normalizeBooleanFlag(applicationConfiguration?.request_email),
        request_phone: normalizeBooleanFlag(applicationConfiguration?.request_phone),
        promocode: normalizeBooleanFlag(applicationConfiguration?.promocode),
        submit_action: applicationConfiguration?.submit_action,
        submit_name: 'Back to site',
        cc_form_provider: applicationConfiguration?.cc_form_provider,
        ...(last_transaction && { last_transaction }),
        ...(customer && {
          customer: {
            ...customer,
            variable1: sessionInformation?.variable1,
            variable2: sessionInformation?.variable2,
            variable3: sessionInformation?.variable3,
            dod_acknowledgement_required: normalizeBooleanFlag(
              applicationConfiguration?.dod_acknowledgement_required,
            ),
            balance: sessionInformation?.balance,
          },
        }),
        ...(promocodeOptions.length > 0 && { promocode_options: promocodeOptions }),
        card_brand: '',
        created_at: null,
        descriptor: null,
        fee_amount: null,
        is_encrypted_input_names: 0,
        promocode_title: null,
        status: 0,
        available_currencies: mapAvailableCurrenciesFees(available_currencies),
      }
    : {
        status: 2,
        submit_action: applicationConfiguration?.submit_action,
        submit_name: 'Back to site',
        title: 'No available payment methods',
        resultPageType: 'no_payment_methods',
        // The tab this empty reply belongs to, so a tab switch that lands on a
        // tab with no currencies can still bring the profile in step with it.
        transaction_type: activeTransactionType,
        intent: activeIntent,
      };
};

export const mapGetSessionTransactionGraphQlData = (graphQLData) => {
  const {
    initTheme: { sessionInformation, applicationConfiguration },
  } = store.getState();

  const currentProfileInfo = store.getState().profileInfo || {};
  const { transaction = {} } = graphQLData;
  const base_amount = transaction.amount ?? sessionInformation?.amount ?? '';
  const base_currency = transaction.currency ?? sessionInformation?.currency ?? '';
  const currency = transaction.charge_currency;
  const chargeCurrency = transaction.charge_currency;
  const isCurrencyConversion = base_currency && chargeCurrency && base_currency !== chargeCurrency;

  const retrySettings = transaction.retry_settings || {};
  const paymentInfo = retrySettings?.payment_information || {};
  const baseCurrencyMinLimit =
    paymentInfo?.min_amount && isCurrencyConversion
      ? round(paymentInfo.min_amount / paymentInfo.rate, chargeCurrency)
      : paymentInfo?.min_amount;
  const baseCurrencyMaxLimit =
    paymentInfo?.max_amount && isCurrencyConversion
      ? round(paymentInfo.max_amount / paymentInfo.rate, chargeCurrency)
      : paymentInfo?.max_amount;

  const retry_settings = transaction.retry_settings
    ? {
        show_amount_field: transaction.retry_settings.show_amount_field ? 1 : 0,
        predefined_options: transaction.retry_settings.predefined_options || [],
        payment_information: {
          base_currency: paymentInfo.base_currency,
          currency_list: paymentInfo.currency_list,
          rate: paymentInfo.rate,
          spread_rate: paymentInfo.spread_rate ?? false,
          min_amount: baseCurrencyMinLimit,
          max_amount: baseCurrencyMaxLimit,
        },
        fees: transaction.retry_settings?.fees,
      }
    : {};

  const getFallbackNewAttemptValue = () => {
    if (transaction.status === 'declined' || transaction.status === 'error') return 1;
    return 0;
  };

  return {
    ...currentProfileInfo,
    transaction: {
      status: transaction.status,
      trace_id: transaction.trace_id,
      currency: transaction.currency,
      amount: transaction.amount,
      transaction_id: transaction.transaction_id,
      payment_processor: transaction.payment_processor,
      error_message: transaction.error_message,
      charge_amount: transaction.charge_amount,
      charge_currency: transaction.charge_currency,
      is_crypto: normalizeBooleanFlag(transaction.is_crypto),
      payment_method: transaction.payment_method,
      payment_method_key: transaction.payment_method_key,
      message: transaction.message,
      descriptor: transaction.descriptor,
      created_at: transaction.created_at,
      fee_amount: transaction.fee_amount,
      card_brand: transaction.card_brand,
      promocode_title: transaction.promocode_title,
      card_number: transaction.card_number,
    },
    last_transaction: {
      amount: transaction.amount,
      currency: transaction.currency,
    },
    base_currency: base_currency,
    base_amount: base_amount,
    ob_decline_recovery: normalizeBooleanFlag(transaction.ob_decline_recovery),
    retry_with_apm: normalizeBooleanFlag(transaction.retry_with_apm),
    ob_additional_fields_filled: transaction.ob_additional_fields_filled || 0,
    transaction_type: transaction.transaction_type,
    session_type: sessionInformation?.session_type,
    new_attempt: transaction.new_attempt ?? getFallbackNewAttemptValue(),
    hide_back_button: normalizeBooleanFlag(applicationConfiguration.hide_back_button),
    final_page_email: null,
    final_page_phone: null,
    final_page_chat: null,
    new_attempt_same_details: transaction.new_attempt_same_details,
    processing_in_progress: transaction.processing_in_progress,
    status: 2,
    title: transaction.message,
    submit_action: transaction.submit_action,
    submit_name: transaction.submit_name || 'Done',
    remove_cancel_option_partial_approved:
      applicationConfiguration.remove_cancel_option_partial_approved,
    retry_settings: retry_settings,
  };
};

export const mapGetGatewaysGraphQlData = (graphQLData) => {
  const {
    initTheme: {
      theme_editor_settings: {
        customization_settings: { layout, currency_display_mode } = {},
      } = {},
      applicationConfiguration = {},
    } = {},
    payData: { currency: payDataCurrency } = {},
    profileInfo: { base_currency = '' } = {},
  } = store.getState();

  const isAmountFirstLayout = layout === 'amount-first';
  const selectedCurrency = payDataCurrency || base_currency || null;
  const currencyDisplayMode = currency_display_mode;
  const shouldSendCurrency =
    isAmountFirstLayout && currencyDisplayMode !== 'base_currency' && !!selectedCurrency;

  const payment_method_groups = graphQLData?.payment_method_groups || [];
  const selectPaymentInformation = (payment_information) => {
    const asArray = Array.isArray(payment_information)
      ? payment_information
      : payment_information
        ? [payment_information]
        : [];

    if (!asArray.length) return {};

    let selected;
    if (shouldSendCurrency && selectedCurrency) {
      selected = asArray.find((info) => info?.currency === selectedCurrency);
    }
    if (!selected && base_currency) {
      selected = asArray.find((info) => info?.currency === base_currency);
    }
    if (!selected) {
      selected = asArray[0];
    }

    const currency_list = asArray.reduce((acc, info) => {
      if (info?.currency) {
        acc[info.currency] = {
          min_amount: info?.limits?.min_amount ?? null,
          max_amount: info?.limits?.max_amount ?? null,
          rate: info?.rate ?? null,
          crypto: info?.limits?.crypto ?? 0,
        };
      }
      return acc;
    }, {});

    return { ...selected, currency_list };
  };

  const gateways = payment_method_groups.flatMap(({ key, title, payment_methods = [] }) =>
    payment_methods.map((gateway = {}) => {
      const selectedPaymentInformation = selectPaymentInformation(gateway.payment_information);
      const selectedCurrencyCode = selectedPaymentInformation?.currency;

      return {
        group_key: key,
        group_title: title,
        option_id: gateway.option_id,
        option_type: gateway.option_type,
        payment_method_key: gateway.key,
        payment_method_type: gateway.option_type,
        title: gateway.title,
        image: gateway.image,
        image_dark_mode: gateway.image_dark_mode,
        payout_condition: gateway.payout_condition,
        payment_information: selectedPaymentInformation || {},
        additional_fields: gateway.additional_fields || [],
        payment_rails: gateway.payment_rails || [],
        recently_used: gateway.recently_used || false,
        recently_used_template: gateway.recently_used_template || null,
        gateway_details: {
          payment_information: selectedPaymentInformation || {},
          additional_fields: gateway.additional_fields || [],
          payment_rails: gateway.payment_rails || [],
          recently_used_template: gateway.recently_used_template || null,
          show_amount: gateway.show_amount ? 1 : 0,
          is_obt_supported: gateway.is_obt_supported,
          fees: {
            [selectedCurrencyCode]: selectedPaymentInformation?.fees || [],
          },
        },
        processor_type: null,
        card_types: [],
        fees: {
          [selectedCurrencyCode]: selectedPaymentInformation?.fees || [],
        },
        quick_deposit: null,
        is_obt_supported: gateway.is_obt_supported,
        show_amount: gateway.show_amount ? 1 : 0,
        maintenance: gateway.maintenance
          ? {
              ...gateway.maintenance,
              enabled: normalizeBooleanFlag(gateway.maintenance?.enabled),
            }
          : null,
      };
    }),
  );

  const {
    send_get_available_gateways_request = 0,
    is_bdcc_enabled = 0,
    is_bdcc_window = 0,
    is_new_window_mobile = 0,
  } = applicationConfiguration;

  return gateways?.length
    ? {
        cvv_input_as_buttons: 0,
        gateways,
        is_bdcc_enabled: is_bdcc_enabled,
        is_bdcc_window: is_bdcc_window,
        is_encrypted_input_names: 0,
        is_new_window_mobile: is_new_window_mobile,
        send_get_available_gateways_request: send_get_available_gateways_request,
        status: 0,
        title: 'How would you like to pay?',
      }
    : {
        status: 2,
        submit_action: applicationConfiguration?.submit_action,
        submit_name: 'Back to site',
        title: 'No available payment methods',
        resultPageType: 'no_payment_methods',
      };
};

export const mapDynamicOptionsGraphQLToRestPayload = (data = {}) => {
  const raw = data.dynamic_options;
  let dynamic_field_options = {};

  if (raw == null || raw === '') {
    return { status: ResponseStatus.OK, dynamic_field_options };
  }

  if (typeof raw !== 'string') {
    throw new Error('dynamic_options must be a JSON string');
  }

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error('dynamic_options is not valid JSON');
  }

  if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('dynamic_options JSON must be a plain object');
  }

  dynamic_field_options = parsed;
  return {
    status: ResponseStatus.OK,
    dynamic_field_options,
  };
};

export const mapGetGatewayDetailsGraphQlData = (data = {}) => {
  const { option_type, option_id, payment_information, graphQLGateway } = data;
  let params = { option_type, option_id };

  const {
    initTheme: {
      theme_editor_settings: { customization_settings: { layout } = {} },
    },
    profileInfo: { available_currencies },
  } = store.getState();

  const { processor_type, payment_method_key, recently_used_template } = data;
  const currencyName = data?.payment_information?.currency || '';
  const rate =
    data?.payment_information?.rate ??
    available_currencies?.currencies?.find((currency) => currency.code === currencyName)?.rate ??
    null;
  // PRX-24667: a native-SDK method whose details did not load keeps the ids,
  // key and title of the selected list item, so it reaches its own error state
  // instead of the fatal error page. Other methods keep the previous behavior.
  const isNativeSdkListFallback =
    !graphQLGateway?.payment_method_details && isNativeSdkMethodKey(payment_method_key);
  const graphQLData = isNativeSdkListFallback
    ? { option_id, option_type, title: data?.title }
    : graphQLGateway?.payment_method_details;

  const finalPaymentMethodType = graphQLData?.type || '';
  let finalProcessorType = processor_type || data?.processor_type;
  if (!finalProcessorType && graphQLData?.processor_type === MANUAL_BANK_WIRE_PROCESSOR_TYPE) {
    finalProcessorType = MANUAL_BANK_WIRE_PROCESSOR_TYPE;
  }
  if (!finalProcessorType) {
    if (finalPaymentMethodType === 'cc') {
      finalProcessorType = 'pp';
    } else if (finalPaymentMethodType === 'ewallet') {
      finalProcessorType = 'apm';
    }
  }

  let gatewayData = {
    image: data?.image || null,
    title: graphQLData.title || '',
    image_dark_mode: data?.image_dark_mode || '',
    payout_condition: graphQLData.payout_condition || '',
    additional_image: graphQLData.additional_image || null,
    additional_image_dark_mode: '',
    option_id: graphQLData.option_id,
    option_type: graphQLData.option_type,
    additional_fields: graphQLData.additional_fields || [],
    templates: graphQLData.templates || {},
    payment_information: {
      currency: currencyName,
      rate: rate,
      base_currency: payment_information?.currency || '',
      currency_list: data?.payment_information?.currency_list || {
        [currencyName]: {
          min_amount: data?.payment_information?.limits?.min_amount || null,
          max_amount: data?.payment_information?.limits?.max_amount || null,
          rate: rate,
          crypto: data?.payment_information?.limits?.crypto || 0,
        },
      },
      min_amount: data?.payment_information?.limits?.min_amount || 0,
      max_amount: data?.payment_information?.limits?.max_amount || 0,
    },
    processor_type: finalProcessorType || '',
    payment_method_type: finalPaymentMethodType,
    payment_method_key: payment_method_key || data?.payment_method_key || graphQLData?.key,
    card_types: Array.isArray(data?.payment_rails)
      ? data.payment_rails.map(({ title }) => title)
      : [],
    card_tokenization_options: graphQLData?.card_tokenization_options,
    native_sdk_configurations: graphQLData?.native_sdk_configurations,
    allow_new_card: graphQLData.allow_new_card,
    fees: {
      [currencyName]: data?.payment_information?.fees || [],
    },
    redirection_type: graphQLData.redirection_type,
    show_amount: normalizeBooleanFlag(graphQLData.show_amount),
    window_size_width: graphQLData.window_size_width || null,
    window_size_height: graphQLData.window_size_height || null,
    is_spread_amount: graphQLData.is_spread_amount || false,
    remove_customer_data: '0',
    time_sample_duration_of_ewallet_templates: '0',
    time_sample_duration_of_credit_card_templates: '0',
    is_obt_supported: graphQLData.is_obt_supported || 0,
    recently_used: graphQLData.recently_used || null,
    recently_used_template: graphQLData.recently_used_template || null,
    key: graphQLData.key,
    status: ResponseStatus.OK,
    skip_customer_details:
      finalPaymentMethodType === 'cc' ? false : graphQLData.skip_customer_details,
  };
  if (recently_used_template) {
    gatewayData.recently_used_template = recently_used_template;
  }
  return gatewayData;
};

export const mapGetGatewayDetailsPanoramicGraphQlData = (data = {}) => {
  const {
    initTheme: { applicationConfiguration = {}, sessionInformation = {} },
  } = store.getState();
  const { graphQLGateway = {}, gateways } = data;
  // PRX-24667: see mapGetGatewayDetailsGraphQlData -- empty details of a
  // native-SDK method fall back to the selected list item.
  const isNativeSdkListFallback =
    !graphQLGateway?.payment_method_details && isNativeSdkMethodKey(data.payment_method_key);
  const paymentMethodDetails = isNativeSdkListFallback
    ? {
        option_id: data.option_id,
        option_type: data.option_type,
        key: data.payment_method_key,
        title: data.title,
      }
    : graphQLGateway?.payment_method_details || {};
  const selectedGateway =
    gateways?.find((gateway) => gateway.option_id === paymentMethodDetails.option_id) || {};

  let templatesObject = {};

  try {
    templatesObject = Object.fromEntries(
      paymentMethodDetails.templates?.map((template) => [
        template.label,
        { ...JSON.parse(template.data) },
      ]) || [],
    );
  } catch (e) {}

  return {
    status: 0,
    option_id: paymentMethodDetails.option_id,
    option_type: paymentMethodDetails.option_type,
    payment_method_key: paymentMethodDetails.key,
    payment_method_type: paymentMethodDetails.type,
    processor_type: paymentMethodDetails.processor_type,
    remove_customer_data: normalizeBooleanFlag(applicationConfiguration.remove_customer_data),
    title: paymentMethodDetails.title ?? '',
    payout_condition: paymentMethodDetails.payout_condition,
    redirection_type: paymentMethodDetails.redirection_type,
    window_size_width: paymentMethodDetails.window_size_width,
    window_size_height: paymentMethodDetails.window_size_height,
    allow_new_card: paymentMethodDetails.allow_new_card ?? false,
    card_types: paymentMethodDetails.payment_rails?.map(({ title }) => title) || [],
    card_tokenization_options: paymentMethodDetails?.card_tokenization_options,
    native_sdk_configurations: paymentMethodDetails?.native_sdk_configurations,
    additional_fields: normalizeAdditionalFields(paymentMethodDetails.additional_fields),
    templates: templatesObject,
    payment_information: {
      base_currency: sessionInformation.currency,
      currency_list: paymentMethodDetails.payment_information?.currency_list,
      max_amount: paymentMethodDetails.payment_information?.limits?.max_amount,
      min_amount: paymentMethodDetails.payment_information?.limits?.min_amount,
      is_crypto: paymentMethodDetails.payment_information?.is_crypto,
    },
    fees: paymentMethodDetails.payment_information?.fees,
    image: selectedGateway.image,
    image_dark_mode: selectedGateway.image_dark_mode,
    additional_image: selectedGateway.additional_image ?? '',
    additional_image_dark_mode: selectedGateway.additional_image_dark_mode ?? '',
    skip_customer_details: normalizeBooleanFlag(paymentMethodDetails.skip_customer_details),
    show_amount: normalizeBooleanFlag(paymentMethodDetails.show_amount),
  };
};

export const mapGetGatewaysPanoramicGraphQlData = (graphQLData) => {
  const state = store.getState();
  const {
    initTheme: {
      applicationConfiguration = {},
      sessionInformation,
      theme_editor_settings: {
        customization_settings: { payment_methods_view, payment_instrument_display } = {},
      } = {},
    } = {},
  } = state;
  const isModernDropdown = payment_methods_view === 'modern_dropdown';
  const isPaymentListFirst = payment_instrument_display === 'list_first';
  const isRecentlyUsedTemplatesEnabled = isModernDropdown && isPaymentListFirst;

  const payment_method_groups = graphQLData?.payment_method_groups || [];
  const first_gateway_details = graphQLData?.first_gateway_details || null;

  const gateways = payment_method_groups.flatMap(({ key, title, payment_methods = [] }) =>
    payment_methods.map((gateway = {}, index) => {
      let templatesObject = {};

      try {
        templatesObject = Object.fromEntries(
          first_gateway_details.templates?.map((template) => [
            template.label,
            { ...JSON.parse(template.data) },
          ]) || [],
        );
      } catch (e) {}
      return {
        additional_image: gateway.additional_image,
        additional_image_dark_mode: gateway.additional_image_dark_mode,
        card_types: gateway.payment_rails?.map((item) => item.title) || [],
        ...(index === 0 && first_gateway_details
          ? {
              gateway_details: {
                additional_fields: normalizeAdditionalFields(
                  first_gateway_details.additional_fields,
                ),
                card_tokenization_options: first_gateway_details?.card_tokenization_options,
                native_sdk_configurations: first_gateway_details?.native_sdk_configurations,
                additional_image: gateway.additional_image,
                additional_image_dark_mode: gateway.additional_image_dark_mode,
                allow_new_card: first_gateway_details.allow_new_card,
                card_types: gateway.payment_rails?.map((item) => item.title) || [],
                fees: first_gateway_details.payment_information.fees,
                image: gateway.image,
                image_dark_mode: gateway.image_dark_mode,
                redirection_type: first_gateway_details.redirection_type,
                is_spread_amount: first_gateway_details.is_spread_amount,
                option_id: gateway.option_id,
                option_type: gateway.option_type,
                payment_information: {
                  ...first_gateway_details.payment_information,
                  currency_list: first_gateway_details.payment_information?.currency_list,
                  max_amount: first_gateway_details.payment_information?.limits?.max_amount,
                  min_amount: first_gateway_details.payment_information?.limits?.min_amount,
                },
                payment_method_key: gateway.key,
                payment_method_type: gateway.payment_method_type,
                payout_condition: gateway.payout_condition,
                processor_type: first_gateway_details.processor_type,
                remove_customer_data: normalizeBooleanFlag(
                  applicationConfiguration?.remove_customer_data,
                ),
                show_amount: normalizeBooleanFlag(first_gateway_details.show_amount),
                skip_customer_details: first_gateway_details.skip_customer_details,
                status: 0,
                templates: templatesObject,
                title: gateway.title,
                window_size_width: first_gateway_details.window_size_width,
                window_size_height: first_gateway_details.window_size_height,
              },
            }
          : {}),
        image: gateway.image,
        image_dark_mode: gateway.image_dark_mode,
        is_obt_supported: normalizeBooleanFlag(gateway.is_obt_supported),
        option_id: gateway.option_id,
        option_type: gateway.option_type,
        ...(isRecentlyUsedTemplatesEnabled && {
          recently_used: gateway.recently_used || false,
          recently_used_template: gateway.recently_used_template || null,
        }),
        payment_information: {
          currency_list: Array.isArray(gateway.payment_information)
            ? Object.fromEntries(
                gateway.payment_information?.map((item) => [
                  item.currency,
                  {
                    min_amount: item.limits.min_amount,
                    max_amount: item.limits.max_amount,
                    rate: item.rate,
                  },
                ]),
              )
            : null,
          fees: Array.isArray(gateway.payment_information)
            ? Object.fromEntries(
                gateway.payment_information?.map((item) => [item.currency, item.fees]) || [],
              )
            : null,
        },
        payment_method_key: gateway.key,
        payment_method_type: gateway.payment_method_type,
        title: gateway.title,
        maintenance: gateway.maintenance
          ? {
              ...gateway.maintenance,
              enabled: normalizeBooleanFlag(gateway.maintenance?.enabled),
            }
          : null,
      };
    }),
  );
  return gateways?.length
    ? {
        status: 0,
        title: 'How would you like to pay?',
        is_new_window_mobile: normalizeBooleanFlag(applicationConfiguration?.is_new_window_mobile),
        is_bdcc_enabled: normalizeBooleanFlag(applicationConfiguration?.is_bdcc_enabled),
        is_bdcc_window: normalizeBooleanFlag(applicationConfiguration?.is_bdcc_window),
        send_get_available_gateways_request: normalizeBooleanFlag(
          applicationConfiguration?.send_get_available_gateways_request,
        ),
        cvv_input_as_buttons: 0,
        gateways,
      }
    : {
        status: 2,
        submit_action: applicationConfiguration?.submit_action,
        submit_name: 'Back to site',
        title: 'No available payment methods',
        resultPageType: 'no_payment_methods',
        // The tab this empty reply belongs to, like the customer-data "no
        // payment methods" result.
        transaction_type: resolveTransactionType(
          getActiveIntent(state),
          sessionInformation?.transaction_type,
        ),
        intent: getActiveIntent(state),
      };
};

export const mapGetWithdrawalRequestsGraphQlData = (graphQLData = {}) => {
  return (
    graphQLData.withdrawal_requests?.map((request) => ({
      ...request,
      gateway: request.payment_method?.title,
      payment_method: {
        key: request.payment_method?.key,
        title: request.payment_method?.title,
      },
    })) || []
  );
};
